import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import MetricsGate from 'components/MetricsGate';

import Board from './Board';
import useQuickFilters from '../../hooks/useQuickFilters';
import toFilterInput from '../../toFilterInput';
import BoardContext, { toDateRange } from 'contexts/BoardContext';
import BonusPeriodsContext from '../../contexts/BonusPeriodsContext';
import usePopup from '../../hooks/usePopup';
import BoardSlideOutContext from 'contexts/BoardSlideOutContext';
import FlexCentered from '../Common/FlexCentered';
import Loading from '../Loading';
import AnalyticsContext from '../../contexts/AnalyticsContext';
import { useLocation, useNavigate } from 'react-router-dom';
import type { GridApi, ModelUpdatedEvent } from 'ag-grid-community';
import CurrentUserContext from '../../contexts/CurrentUserContext';
import isDriverPerformanceBoard from '../../isDriverPerformanceBoard';
import useContentViewTracking from '../../hooks/useContentViewTracking';
import DateInputContext from '../../contexts/DateInputContext';
import withDateFilter from '../../hocs/withDateFIlter';
import useDateScope from '../../hooks/useDateScope';
import GqlClientProvider from '../../contextProviders/SplashScreenProviders/UserAndAccountProviders/GqlClientProvider';
import BulkGridActionsProvider from '../../contextProviders/BulkGridActionsProvider';
import useBoardOpenedTracking from '../../hooks/useBoardTracking';
import FilterPlatesProvider from '../../contextProviders/FilterPlatesProvider';
import useReasonCodes from '../../hooks/useReasonCodes';
import { usePlatesToFilterInput } from 'screens/GoalShow/useGoalData';
import useToFixedFilters from 'hooks/useToFixedFilters';

const getVisType = (board: GeneralBoard | DriverPerformanceBoard) => {
  if (isDriverPerformanceBoard(board)) {
    return 'driver-performance-board';
  }
  return 'general-board';
};

const getParam = (
  search: string,
  drillDownField: string,
): string | undefined => {
  const urlParams = new URLSearchParams(search);
  if (urlParams.has(drillDownField)) {
    return urlParams.get(drillDownField) as string;
  }
  return undefined;
};

const useSortOverride = (board: DriverPerformanceBoard | GeneralBoard) => {
  const getSort = useCallback(() => {
    return [
      {
        field: isDriverPerformanceBoard(board)
          ? 'driver'
          : board.drillDownField,
        direction: 'asc' as SortBy,
      },
    ];
  }, [board]);
  const [sortOverride, setSortOverride] = useState<SimpleGridSort[]>(() =>
    getSort(),
  );

  useEffect(() => {
    setSortOverride(getSort());
  }, [getSort]);

  return sortOverride;
};

const BoardContainer = ({
  board,
  initialRunTimeFilters,
  initialScopeFilters,
  isBonusPeriodDriven,
  isPeriodSelectorForced,
}: {
  board: DriverPerformanceBoard | GeneralBoard;
  initialRunTimeFilters: FilterPlateType[];
  initialScopeFilters: FilterPlateType[];
  isBonusPeriodDriven?: boolean;
  isPeriodSelectorForced?: boolean;
}) => {
  useContentViewTracking({
    type: 'board',
    name: board.name,
    typeId: board.id,
  });
  const navigate = useNavigate();
  const location = useLocation();
  const { setDateRange, setDataTypes } = useContext(DateInputContext);
  const dateRange = useDateScope({});
  const currentUser = useContext(CurrentUserContext);
  const { selectedBonusPeriod } = useContext(BonusPeriodsContext);
  const { trackEvent } = useContext(AnalyticsContext);
  const [gridApi, setGridApi] = useState<GridApi>();
  const [exportHandle, setExportHandle] = useState<DataTable.ExportHandle>();
  const quickFilters = useQuickFilters(
    `${board.dataType} - board: ${board.id}`,
  );
  const {
    isOpen: isSlideOutOpen,
    open: openSlideOut,
    close: closeSlideOut,
  } = usePopup();
  const [selectedFieldValue, setSelectedFieldValue] = useState<
    string | undefined
  >();
  const [drillDowns, setDrillDowns] = useState<FilterPlateType[]>(
    () => initialRunTimeFilters,
  );
  const [selectedQuickFilters, setSelectedQuickFilters] = useState<
    LegacySavedFilter[]
  >([]);
  const [netQuickFilters, setNetQuickFilters] = useState<FilterInput[]>([]);
  const reasonCodes = useReasonCodes(board.dataType);
  const [gridRequiresRefresh, setGridRequiresRefresh] =
    useState<boolean>(false);
  const [dataTypes] = useState<string[]>([board.dataType]);
  const sortOverride = useSortOverride(board);
  const platesToFilterInput = usePlatesToFilterInput();
  const toDrillDowns = useToFixedFilters();

  const driverUrlParam = getParam(
    window.location.search,
    isDriverPerformanceBoard(board) ? 'driver' : board.drillDownField,
  );

  useEffect(() => {
    if (driverUrlParam) {
      setSelectedFieldValue(driverUrlParam);
      openSlideOut();
    } else {
      closeSlideOut();
    }
  }, [closeSlideOut, driverUrlParam, openSlideOut]);

  useEffect(() => {
    setDataTypes([board.dataType]);
    setDateRange(
      selectedBonusPeriod ? toDateRange(selectedBonusPeriod) : undefined,
    );
  }, [board.dataType, selectedBonusPeriod, setDataTypes, setDateRange]);

  const onSlideOutClosed = useCallback(() => {
    const hasQueryParams = window.location.search.length > 0;
    if (hasQueryParams) {
      navigate(-1);
    }
    setTimeout(() => {
      if (gridApi) {
        gridApi.autoSizeAllColumns(true);
        gridApi.sizeColumnsToFit();
      }
    }, 100);
  }, [gridApi, navigate]);

  const { onManualFilterChanged, onQuickFilterChanged } =
    useBoardOpenedTracking({ board, onSlideOutClosed });

  const updateHighlightedRows = useCallback(
    (event: ModelUpdatedEvent) => {
      event.api.forEachNode((node) => {
        if (!node.data) {
          return;
        }
        const f =
          node.data[
            isDriverPerformanceBoard(board) ? 'driver' : board.drillDownField
          ];
        if (f !== selectedFieldValue) {
          node.setSelected(false);
        } else {
          node.setSelected(true);
        }
      });
    },
    [board, selectedFieldValue],
  );

  useEffect(() => {
    if (gridApi) {
      gridApi.addEventListener('modelUpdated', updateHighlightedRows);
      return () => {
        if (gridApi.isDestroyed()) {
          return;
        }
        gridApi.removeEventListener('modelUpdated', updateHighlightedRows);
      };
    }
  }, [gridApi, updateHighlightedRows]);

  const selectFieldValue = useCallback(
    (newFieldValue: string) => {
      setSelectedFieldValue(newFieldValue);
      const fieldKey = isDriverPerformanceBoard(board)
        ? 'driver'
        : board.drillDownField;
      const existingParams = new URLSearchParams(location.search);
      existingParams.set(fieldKey, newFieldValue);
      navigate(
        { search: existingParams.toString() },
        { replace: location.search.length > 0 },
      );

      openSlideOut();
      setTimeout(() => {
        if (gridApi) {
          gridApi.autoSizeAllColumns(true);
          gridApi.sizeColumnsToFit();
        }
      }, 100);
      trackEvent('Board - Slide out opened', {
        boardName: board.name,
        boardId: board.id,
        boardDataType: board.dataType,
      });
    },
    [board, navigate, location.search, openSlideOut, trackEvent, gridApi],
  );

  // Filters which ride along with the quick filters, but can't be toggled
  const fixedQuickFilters = useMemo(
    (): FilterInput[] => [
      ...(board.dataSourceFilterPlates
        ? [platesToFilterInput(board.dataSourceFilterPlates)]
        : []),
      ...(initialScopeFilters
        ? [platesToFilterInput(initialScopeFilters)]
        : []),
    ],
    [board.dataSourceFilterPlates, initialScopeFilters, platesToFilterInput],
  );

  useEffect(() => {
    setNetQuickFilters([
      ...selectedQuickFilters.map((qf) =>
        toFilterInput(qf, window.emptyArray, toDrillDowns),
      ),
      ...fixedQuickFilters,
    ]);
  }, [fixedQuickFilters, selectedQuickFilters, toDrillDowns]);

  if (!dateRange) {
    return (
      <FlexCentered>
        <Loading />
      </FlexCentered>
    );
  }

  return (
    <MetricsGate content={board} contentType="Board">
      <FilterPlatesProvider
        drillDowns={drillDowns}
        setDrillDowns={setDrillDowns}
        scope={initialScopeFilters}
        dataTypes={dataTypes}
        quickFilters={netQuickFilters}
      >
        <BoardSlideOutContext.Provider
          value={{
            sections: board.sidePanel,
            isOpen: isSlideOutOpen,
            close: onSlideOutClosed,
            selectedFieldValue,
            drillDownField: isDriverPerformanceBoard(board)
              ? 'driver'
              : board.drillDownField,
            selectFieldValue,
            gridRequiresRefresh,
            setGridRequiresRefresh,
          }}
        >
          <BoardContext.Provider
            value={{
              board,
              reasonCodes,
              quickFilters,
              selectedQuickFilters,
              setSelectedQuickFilters,
              fixedQuickFilters,
              isBoardOwner: isDriverPerformanceBoard(board)
                ? board.assignedToId.includes(currentUser.id)
                : false,
            }}
          >
            <BulkGridActionsProvider>
              <GqlClientProvider
                visId={board.id}
                visName={board.name}
                visType={getVisType(board)}
              >
                <Board
                  board={board}
                  sortOverride={sortOverride}
                  onQuickFilterChanged={onQuickFilterChanged}
                  onManualFilterChanged={onManualFilterChanged}
                  gridApi={gridApi}
                  setGridApi={setGridApi}
                  exportHandle={exportHandle}
                  setExportHandle={setExportHandle}
                  isBonusPeriodDriven={isBonusPeriodDriven}
                  isPeriodSelectorForced={isPeriodSelectorForced}
                />
              </GqlClientProvider>
            </BulkGridActionsProvider>
          </BoardContext.Provider>
        </BoardSlideOutContext.Provider>
      </FilterPlatesProvider>
    </MetricsGate>
  );
};

export default withDateFilter(BoardContainer, {});
